
import { supabase } from '@/lib/supabase';

// Supabase configuration values - avoiding process.env which causes browser errors
const SUPABASE_URL = import.meta.env?.VITE_SUPABASE_URL || 'https://muoihzfomfercsujyyta.supabase.co';
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im11b2loemZvbWZlcmNzdWp5eXRhIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM4Nzc5ODYsImV4cCI6MjA1OTQ1Mzk4Nn0.yOlwlmjSRiqWhjEPWgCg_jvqLEgvfzQacDhezJlQZ98";

/**
 * Utility for calling Supabase Edge Functions with proper authentication
 * Automatically handles auth tokens and provides error handling
 */

export interface FunctionCallOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  queryParams?: Record<string, string>;
}

/**
 * Call a Supabase Edge Function with proper authentication
 * 
 * @param functionName - The name of the Supabase function to call
 * @param options - Options for the function call (method, body, headers, queryParams)
 * @returns The parsed JSON response from the function
 */
export async function callFunction<T = any>(
  functionName: string,
  options: FunctionCallOptions = {}
): Promise<T> {
  try {
    console.log(`Calling Edge Function: ${functionName}`);
    
    // Extract options
    const {
      method = 'POST',
      body,
      headers = {},
      queryParams = {}
    } = options;
    
    // Get auth token
    const { data: sessionData } = await supabase.auth.getSession();
    const authToken = sessionData?.session?.access_token;
    
    // For better debugging, log auth status without exposing the token
    if (!authToken) {
      console.warn('No auth token available for Edge Function call. Proceeding with anonymous call...');
    } else {
      console.log('Auth token available for function call:', !!authToken);
    }
    
    // Build URL with query parameters
    let url = `${SUPABASE_URL}/functions/v1/${functionName}`;
    
    if (Object.keys(queryParams).length > 0) {
      const params = new URLSearchParams();
      Object.entries(queryParams).forEach(([key, value]) => {
        params.append(key, value);
      });
      url = `${url}?${params.toString()}`;
    }
    
    console.log(`Edge Function URL: ${url}`);
    
    // Set up headers with fallback to anonymous access if no auth token
    const requestHeaders = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
      ...headers
    };
    
    // Only add Authorization header if we have a token
    if (authToken) {
      requestHeaders['Authorization'] = `Bearer ${authToken}`;
    }
    
    // Make the request
    const response = await fetch(url, {
      method,
      headers: requestHeaders,
      body: body ? JSON.stringify(body) : undefined
    });
    
    // Log response status
    console.log(`Edge Function response status: ${response.status}`);
    
    // Handle response
    if (!response.ok) {
      let errorMessage = `Edge function ${functionName} failed:`;
      
      try {
        // Try to parse error data for more details
        const errorData = await response.json();
        console.log('Edge Function error details:', errorData);
        
        if (errorData.error) {
          errorMessage += ` - ${errorData.error}`;
        }
        if (errorData.message) {
          errorMessage += ` - ${errorData.message}`;
        }
      } catch (parseError) {
        console.error('Could not parse error response:', parseError);
      }
      
      throw new Error(errorMessage);
    }
    
    const responseData = await response.json();
    console.log(`Edge Function ${functionName} completed successfully`);
    return responseData as T;
  } catch (error) {
    console.error(`Error calling Edge Function ${functionName}:`, error);
    throw error;
  }
}

// For backward compatibility with existing code
export const callSupabaseFunction = callFunction;

// Typed convenience methods
export const getFunctionData = <T = any>(functionName: string, queryParams?: Record<string, string>) => 
  callFunction<T>(functionName, { method: 'GET', queryParams });

export const postFunctionData = <T = any>(functionName: string, body: any) => 
  callFunction<T>(functionName, { method: 'POST', body });

// Export default for convenient imports
export default {
  call: callFunction,
  get: getFunctionData,
  post: postFunctionData
};
